// Матрица прав — одна на сервис (FR-015), дословно из contracts/rights-matrix.md 001, строк 002
// (specs/002-platform/contracts/rights-matrix.md), строк 003 и 005 (specs/003-file-to-control-sheet/
// и specs/005-directories/ contracts/api.md, «Матрица прав»).
// Действие разрешено, если хотя бы одна действующая роль пользователя (включая роли по
// замещению) разрешает операцию в области записи (FR-011). Всё, чего нет в матрице, запрещено.
// Исключение — ячейки «есть своя роль»: их даёт только собственное назначение роли, не
// полученное по замещению (FR-018а, FR-018б). Сверх ячеек действует правило «в сеансе»
// (FR-011а): выход, свой профиль, свои уведомления и свои замещения — любому вошедшему.
import { z } from '../kernel/zod.ts';
import type { ProtocolRole } from '../minutes/access.ts';
import { roleInfo, roles } from './roles.ts';
import type { Role } from './roles.ts';

/**
 * Правило области:
 * - all — во всей установке («всё»);
 * - areas — в областях действующих назначений роли («области»);
 * - open — во всей установке, кроме протоколов с общим доступом «Только добавленные» («открытые»,
 *   020: research, Р2);
 * - self — только своя запись («сам»);
 * - grant — только при действующем временном доступе сопровождения («по доступу»);
 * - none — запрещено («—»).
 */
export const scopeRules = ['all', 'open', 'areas', 'self', 'grant', 'none'] as const;

export type ScopeRule = (typeof scopeRules)[number];

export const ScopeRuleSchema = z.enum(scopeRules);

export const scopeRuleLabels = {
  all: 'всё',
  open: 'открытые',
  areas: 'области',
  self: 'сам',
  grant: 'по доступу',
  none: '—',
} as const satisfies Record<ScopeRule, string>;

/** Ячейка матрицы: правило с уточнением или ссылка «как …» на другие операции. */
export type MatrixCell =
  | {
      readonly rule: ScopeRule;
      readonly note?: string;
      /**
       * Правило даёт только собственное назначение роли — роль, полученная по замещению, его
       * не даёт: «сам (есть своя роль)» (FR-018а, FR-018б).
       */
      readonly ownRoleOnly?: true;
    }
  | { readonly rule: 'same_as'; readonly sameAs: readonly string[]; readonly label: string };

type CellInput = MatrixCell | ScopeRule;
type RowCells = readonly [CellInput, CellInput, CellInput, CellInput, CellInput, CellInput];

export interface MatrixRow<O extends string = string> {
  readonly operations: readonly O[];
  /** Пояснение строки, если оно есть в документе: «список и карточка». */
  readonly description?: string;
  readonly cells: Readonly<Record<Role, MatrixCell>>;
}

/** Ячейки — в порядке колонок документа: А, К, В, П, Н, С. */
function row<const O extends readonly string[]>(
  operations: O,
  cells: RowCells,
  description?: string,
): {
  readonly operations: O;
  readonly description?: string;
  readonly cells: Readonly<Record<Role, MatrixCell>>;
} {
  const normalized = Object.fromEntries(
    roles.map((role, i) => {
      const cell = cells[i] ?? 'none';
      return [role, typeof cell === 'string' ? { rule: cell } : cell];
    }),
  ) as Record<Role, MatrixCell>;
  return description === undefined
    ? { operations, cells: normalized }
    : { operations, description, cells: normalized };
}

const ownRole = { rule: 'self', note: 'своя роль' } as const;
/** Есть собственная действующая роль, которую можно передать; роль по замещению — не своя. */
const hasOwnRole = { rule: 'self', note: 'есть своя роль', ownRoleOnly: true } as const;
const asAuditRead = {
  rule: 'same_as',
  sameAs: ['audit.read.content', 'audit.read.access'],
  label: 'как audit.read.*',
} as const;

export const rightsMatrix = [
  row(
    ['auth.session'],
    ['self', 'self', 'self', 'self', 'self', 'self'],
    'вход, выход, выход на всех устройствах',
  ),
  row(
    ['profile.read', 'profile.change_password'],
    ['self', 'self', 'self', 'self', 'self', 'self'],
  ),
  row(['users.read'], ['all', 'none', 'none', 'none', 'none', 'none'], 'список и карточка'),
  row(['users.create', 'users.update'], ['all', 'none', 'none', 'none', 'none', 'none']),
  row(['users.archive', 'users.restore'], ['all', 'none', 'none', 'none', 'none', 'none']),
  row(
    ['users.invite', 'users.reset_password', 'users.unlock', 'users.revoke_sessions'],
    ['all', 'none', 'none', 'none', 'none', 'none'],
  ),
  row(['assignments.read'], ['all', 'self', 'self', 'self', 'self', 'self']),
  row(
    ['assignments.manage'],
    ['all', 'none', 'none', 'none', 'none', 'none'],
    'создать, изменить, архив, вернуть',
  ),
  row(['substitutions.read'], ['all', 'self', 'self', 'self', 'self', 'self']),
  row(['substitutions.create'], ['all', ownRole, ownRole, ownRole, ownRole, 'none']),
  row(
    ['substitutions.cancel'],
    ['all', { rule: 'self', note: 'владелец или заместитель' }, 'self', 'self', 'self', 'none'],
  ),
  row(
    ['substitutions.candidates'],
    ['all', hasOwnRole, hasOwnRole, hasOwnRole, hasOwnRole, 'none'],
    'список кандидатов в заместители',
  ),
  row(['support.read'], ['all', 'none', 'none', 'none', 'none', 'self']),
  row(['support.grant', 'support.revoke'], ['all', 'none', 'none', 'none', 'none', 'none']),
  row(['rights.read'], ['all', 'none', 'none', 'none', 'none', 'none']),
  row(
    ['audit.read.content'],
    ['all', 'areas', 'areas', 'areas', 'areas', 'grant'],
    'записи по документам и пунктам',
  ),
  row(
    ['audit.read.access'],
    ['all', 'none', 'none', 'none', 'none', 'none'],
    'записи о пользователях и доступе',
  ),
  row(['audit.export'], [asAuditRead, asAuditRead, asAuditRead, asAuditRead, asAuditRead, 'grant']),
  row(['notifications.read'], ['self', 'self', 'self', 'self', 'self', 'self']),
  row(
    ['ops.read'],
    ['all', 'none', 'none', 'none', 'none', 'all'],
    'выкатки и проверки восстановления',
  ),
  // 002: экраны сопровождения. Технический журнал, задачи, оповещения и метрики — без
  // персональных данных: временный доступ сопровождению не нужен. Администратор клиента видит
  // только краткую панель.
  row(
    ['ops.health.read'],
    ['all', 'none', 'none', 'none', 'none', 'all'],
    'краткая панель состояния',
  ),
  row(['ops.status.read'], ['none', 'none', 'none', 'none', 'none', 'all'], 'панель состояния'),
  row(['ops.jobs.read'], ['none', 'none', 'none', 'none', 'none', 'all'], 'фоновые задачи'),
  row(['ops.jobs.retry'], ['none', 'none', 'none', 'none', 'none', 'all'], 'повторить задачу'),
  row(['ops.jobs.cancel'], ['none', 'none', 'none', 'none', 'none', 'all'], 'отменить задачу'),
  row(['ops.log.read'], ['none', 'none', 'none', 'none', 'none', 'all'], 'технический журнал'),
  row(['ops.alerts.read'], ['none', 'none', 'none', 'none', 'none', 'all'], 'оповещения'),
  row(
    ['ops.backups.read'],
    ['none', 'none', 'none', 'none', 'none', 'all'],
    'копии и проверки восстановления',
  ),
  row(['ops.metrics.read'], ['none', 'none', 'none', 'none', 'none', 'all'], 'метрики'),
  // 003: документы и таблица открытых пунктов (specs/003-file-to-control-sheet/contracts/api.md,
  // «Матрица прав»). Изменение и архив документа — спецификация реестра документов (принцип XI).
  row(
    ['documents.manage'],
    ['all', 'all', 'areas', 'none', 'none', 'none'],
    'папки, свойства, перемещение и корзина протоколов',
  ),
  row(['documents.create'], ['all', 'all', 'none', 'none', 'none', 'none'], '«Новый протокол»'),
  row(
    ['documents.versions.upload'],
    ['all', 'all', 'areas', 'none', 'none', 'none'],
    '«Новая версия»',
  ),
  // 020: наблюдатель читает протоколы, кроме «Только добавленные» (research, Р2).
  row(
    ['documents.read'],
    ['all', 'all', 'areas', 'all', 'open', 'grant'],
    'список, оригинал, лист контроля',
  ),
  row(
    ['control.read'],
    ['all', 'all', 'areas', 'all', 'open', 'grant'],
    'таблица открытых пунктов',
  ),
  // 005: справочник, настройки, ключи агента (specs/005-directories/contracts/api.md, «Матрица
  // прав»). Область ведущего — записи, упомянутые в документах его областей.
  row(
    ['directory.read'],
    ['all', 'all', 'areas', 'all', 'all', 'grant'],
    'справочник, упоминания, пары, «К разбору», поиск',
  ),
  row(
    ['directory.write'],
    ['all', 'all', 'areas', 'none', 'none', 'none'],
    'создать и дополнить запись, почта, места работы, кураторы, зона, псевдонимы, предложить пару',
  ),
  row(
    ['directory.decide'],
    ['all', 'all', 'none', 'none', 'none', 'none'],
    'пары, склейка, разъединение, подтверждение адреса, «получает письма»',
  ),
  row(['settings.write'], ['all', 'none', 'none', 'none', 'none', 'none'], 'настройки установки'),
  row(['agent_keys.manage'], ['all', 'none', 'none', 'none', 'none', 'none'], 'ключи агента'),
  // 008: канал почты (contracts/api.md 008, «Матрица прав»).
  row(
    ['mail.read'],
    ['all', 'none', 'none', 'none', 'none', 'all'],
    'подключение почты, письма и ответные письма',
  ),
  row(
    ['mail.write'],
    ['all', 'none', 'none', 'none', 'none', 'all'],
    'подключение почты, ключ, проверка, отчёты о доставке, пробное письмо, отключение отправки',
  ),
  // 010: схемы шаблонов и мастер подключения (contracts/api.md 010, «Матрица прав»).
  row(
    ['templates.read'],
    ['all', 'none', 'none', 'none', 'none', 'none'],
    'схемы шаблонов, карта колонок, история версий',
  ),
  row(
    ['templates.write'],
    ['all', 'none', 'none', 'none', 'none', 'none'],
    'мастер подключения, правка, версии, архив и возврат схем',
  ),
  // 011: протокол в системе (contracts/api.md 011, «Матрица прав»; ADR-0033 — полная матрица 017).
  row(
    ['minutes.read'],
    ['all', 'all', 'areas', 'all', 'open', 'grant'],
    'таблица протокола в режиме ведения',
  ),
  row(
    // 018: те же роли; анализ дополнительно ограничен инициатором в обработчике.
    ['minutes.edit'],
    ['all', 'all', 'areas', 'none', 'none', 'none'],
    'команды таблицы: правка ячеек, новая строка, решение родителя',
  ),
  row(
    ['minutes.transfer'],
    ['all', 'all', 'none', 'none', 'none', 'none'],
    'перевод протокола и возврат в режим файла',
  ),
  // 020: доступ к протоколу (contracts/api.md 020, «Матрица прав»; ADR-0039).
  row(
    ['minutes.manage'],
    ['all', 'all', 'areas', 'none', 'none', 'none'],
    'команды протокола целиком: доп. поля, «Скрыть в протоколе», снимок недели',
  ),
  row(
    ['minutes.access.read'],
    ['all', 'all', 'areas', 'all', 'open', 'grant'],
    'список доступа к протоколу',
  ),
  row(
    ['minutes.access.manage'],
    ['all', 'all', 'areas', 'none', 'none', 'none'],
    'выдать, сменить и снять роль в протоколе, общий доступ, пригласить',
  ),
  // 030: «Мои задачи» и «Первое письмо» (contracts/api.md 030, «Матрица прав»; research Р2, Р5).
  // items.own — ещё и правило «в сеансе»: его даёт связь учётной записи с человеком справочника,
  // а не роль; без связи — ACC-008. Сопровождению ячейка — «—», как у замещений.
  row(
    ['items.own'],
    ['self', 'self', 'self', 'self', 'self', 'none'],
    '«свои пункты»: «Мои задачи», ответы и правка своих пунктов',
  ),
  row(
    ['minutes.first_letter'],
    ['all', 'all', 'areas', 'none', 'none', 'none'],
    '«Отправить первое письмо…»: запланировать и отменить',
  ),
  // 033: стоп-кран и рассылка протокола (contracts/api.md 033, «Матрица прав»).
  row(
    ['mail.stop'],
    ['all', 'all', 'none', 'none', 'none', 'none'],
    '«Остановить все сообщения» и «Возобновить»',
  ),
  row(
    ['minutes.mailing'],
    ['all', 'all', 'areas', 'none', 'none', 'none'],
    '«Рассылка протокола…»: просмотр, «Включить в тени», «Выключить рассылку»',
  ),
  row(
    ['minutes.mailing_enable'],
    ['all', 'all', 'none', 'none', 'none', 'none'],
    '«Включить рассылку» протокола',
  ),
  // 032: право руководителя (поправка XII 2.7.0; contracts/api.md 032, «Права»; ADR-0047).
  // items.team — правило «в сеансе», как items.own: его дают связь учётной записи с человеком
  // и кураторство в справочнике, а не роль; только чтение просроченных пунктов людей руководителя.
  row(
    ['items.team'],
    ['self', 'self', 'self', 'self', 'self', 'none'],
    '«Просрочено у ваших сотрудников»: только чтение, только просроченные пункты людей, у которых пользователь — руководитель по дереву (поправка XII 2.7.0)',
  ),
  row(
    ['people.away'],
    ['all', 'all', 'none', 'none', 'none', 'none'],
    '«В отъезде до» другому человеку (вкладка «Ответственные»)',
  ),
] as const;

export type Operation = (typeof rightsMatrix)[number]['operations'][number];

export const operations: readonly Operation[] = rightsMatrix.flatMap((r) => r.operations);

export const OperationSchema = z.enum(operations);

const NONE: MatrixCell = { rule: 'none' };

const cellsByOperation = new Map<string, Readonly<Record<Role, MatrixCell>>>(
  rightsMatrix.flatMap((r) => r.operations.map((op) => [op, r.cells] as const)),
);

export function isOperation(value: string): value is Operation {
  return cellsByOperation.has(value);
}

/**
 * Правило «в сеансе» (FR-011а; contracts/rights-matrix.md): эти операции над своим — выход,
 * профиль и смена пароля, свои уведомления, свои замещения и их отмена — даёт сам сеанс, любому
 * пользователю, даже без ролей и с ролью только по замещению. Правило — «сам»; ячейки матрицы
 * оно не меняет, а дополняет: у кого правило шире («всё»), тот остаётся при нём.
 */
export const sessionOperations = [
  'auth.session',
  'profile.read',
  'profile.change_password',
  'notifications.read',
  'substitutions.read',
  'substitutions.cancel',
  // 030 (research Р2): «свои пункты» — у сеанса со связью с человеком; без связи — ACC-008.
  'items.own',
  // 032: «Просрочено у ваших сотрудников» — у сеанса со связью с человеком-руководителем.
  'items.team',
] as const satisfies readonly Operation[];

/**
 * 030 (research Р1; ADR-0044): что даёт сеанс по ссылке — только эти операции; ролей установки,
 * ролей в протоколах и общего доступа у него нет.
 */
export const linkSessionOperations = [
  'auth.session',
  'profile.read',
  // Свои уведомления: колокольчик шапки по «Табелю» (конституция X), каркас тот же.
  'notifications.read',
  'items.own',
  // 032: руководитель входит по ссылке из копии руководителю (FR-021).
  'items.team',
] as const satisfies readonly Operation[];

const linkSessionOperationSet: ReadonlySet<string> = new Set(linkSessionOperations);

/** Даёт ли сеанс по ссылке эту операцию (030). */
export function isLinkSessionOperation(operation: Operation): boolean {
  return linkSessionOperationSet.has(operation);
}

const sessionOperationSet: ReadonlySet<string> = new Set(sessionOperations);

export function isSessionOperation(operation: Operation): boolean {
  return sessionOperationSet.has(operation);
}

/** Правило, которое даёт сам сеанс: «сам» для операций «в сеансе», иначе «—». */
export function sessionRuleFor(operation: Operation): ScopeRule {
  return isSessionOperation(operation) ? 'self' : 'none';
}

/** Ячейка матрицы как есть; нет строки или роли — «—». */
export function cellFor(role: Role, operation: Operation): MatrixCell {
  const cells = cellsByOperation.get(operation);
  if (!cells || !Object.hasOwn(cells, role)) return NONE;
  return cells[role];
}

/** Подпись ячейки как в документе: «сам (своя роль)», «как audit.read.*». */
export function cellLabel(cell: MatrixCell): string {
  if (cell.rule === 'same_as') return cell.label;
  const label = scopeRuleLabels[cell.rule];
  return cell.note ? `${label} (${cell.note})` : label;
}

const WIDTH: Readonly<Record<ScopeRule, number>> = {
  none: 0,
  self: 1,
  grant: 2,
  areas: 3,
  open: 4,
  all: 5,
};

/** Самое широкое правило из набора; пустой набор — «—». */
export function widestRule(rules: Iterable<ScopeRule>): ScopeRule {
  let widest: ScopeRule = 'none';
  for (const rule of rules) if (WIDTH[rule] > WIDTH[widest]) widest = rule;
  return widest;
}

/**
 * Действующая роль пользователя для проверки прав: просто роль — собственное назначение;
 * { role, bySubstitution: true } — роль, полученная по замещению (FR-019).
 */
export type RoleGrant = Role | { readonly role: Role; readonly bySubstitution: boolean };

function grantParts(grant: RoleGrant): { role: Role; bySubstitution: boolean } {
  return typeof grant === 'string' ? { role: grant, bySubstitution: false } : grant;
}

/**
 * Правило действующей роли для операции, без обращения к базе. Роль по замещению даёт те же
 * правила, что и своя (FR-019), кроме ячеек «есть своя роль» (ownRoleOnly): их даёт только
 * собственное назначение (FR-018а, FR-018б). Для ячейки «как audit.read.*» — самое широкое
 * из правил тех операций: оно решает, доступна ли операция вообще; какие записи попадут
 * в выгрузку, решают правила audit.read.content и audit.read.access по чувствительности
 * записи (auditReadOperation).
 */
export function grantRuleFor(grant: RoleGrant, operation: Operation): ScopeRule {
  const { role, bySubstitution } = grantParts(grant);
  const cell = cellFor(role, operation);
  if (cell.rule === 'same_as') {
    return widestRule(cell.sameAs.filter(isOperation).map((op) => grantRuleFor(grant, op)));
  }
  if (bySubstitution && cell.ownRoleOnly) return 'none';
  return cell.rule;
}

/** Правило собственной роли для операции (ячейка матрицы; «как …» — самое широкое из правил). */
export function ruleFor(role: Role, operation: Operation): ScopeRule {
  return grantRuleFor(role, operation);
}

/**
 * Разные разрешающие правила ролей пользователя для операции и правило «в сеансе» (FR-011а);
 * пусто — запрещено.
 */
export function rulesFor(userRoles: readonly RoleGrant[], operation: Operation): ScopeRule[] {
  const rules = new Set<ScopeRule>();
  for (const grant of userRoles) {
    const rule = grantRuleFor(grant, operation);
    if (rule !== 'none') rules.add(rule);
  }
  const session = sessionRuleFor(operation);
  if (session !== 'none') rules.add(session);
  return [...rules];
}

/** Разрешает ли хотя бы одна роль (или сам сеанс) операцию хоть в какой-то области. */
export function isAllowed(userRoles: readonly RoleGrant[], operation: Operation): boolean {
  return rulesFor(userRoles, operation).length > 0;
}

export type Permissions = Partial<Record<Operation, ScopeRule>>;

/**
 * Разрешённые операции и самое широкое правило по ним — для меню и «Создать ▾»
 * (ответ GET /api/auth/me). Роль по замещению — { role, bySubstitution: true }: ячеек
 * «есть своя роль» она не даёт. Операции «в сеансе» есть у всех (FR-011а). Сервер всё равно
 * проверяет каждое действие по ролям и областям.
 */
export function effectivePermissions(
  userRoles: readonly RoleGrant[],
  protocol: ProtocolGrants = NO_PROTOCOL_GRANTS,
): Permissions {
  const result: Permissions = {};
  for (const op of operations) {
    const rules = rulesFor(userRoles, op);
    // 020: роль в протоколе даёт операцию в протоколах из списка — как «области»; общий доступ —
    // во всех протоколах, кроме «Только добавленные» (research, Р3).
    // 022: внешнему роль в протоколе даёт только externalProtocolOperations, общего доступа нет.
    const byRole = !protocol.external || isExternalProtocolOperation(op);
    if (byRole && protocol.roles.some((role) => protocolRuleFor(role, op) !== 'none')) {
      rules.push('areas');
    }
    if (protocol.company && !protocol.external && protocolRuleFor('company', op) !== 'none') {
      rules.push('open');
    }
    const rule = widestRule(rules);
    if (rule !== 'none') result[op] = rule;
  }
  return result;
}

// --- Роли в протоколе (020: contracts/api.md, «Матрица прав»; research, Р2; ADR-0039) ---

/**
 * Правило роли в протоколе: protocol — протокол целиком; zone — пункты зоны редактора (сервер
 * проверяет каждый затронутый пункт); none — запрещено.
 */
export const protocolRules = ['protocol', 'zone', 'none'] as const;
export type ProtocolRule = (typeof protocolRules)[number];

/**
 * Кому правило второй таблицы: роли в протоколе и company — общий доступ «Все сотрудники компании —
 * читатели» (любой пользователь в сеансе без роли «Сопровождение», протокол не «Только
 * добавленные»).
 */
export const protocolGrantees = ['reader', 'editor', 'zone_editor', 'company'] as const;
export type ProtocolGrantee = (typeof protocolGrantees)[number];
type ProtocolCells = Readonly<Record<ProtocolGrantee, ProtocolRule>>;

/** Таблица ролей в протоколе: строки — операции протокола, ячейки — правила. */
export const protocolRightsMatrix: readonly {
  readonly operations: readonly Operation[];
  readonly cells: ProtocolCells;
}[] = [
  {
    operations: ['documents.read', 'control.read', 'minutes.read', 'minutes.access.read'],
    cells: { reader: 'protocol', editor: 'protocol', zone_editor: 'protocol', company: 'protocol' },
  },
  {
    operations: ['minutes.edit'],
    cells: { reader: 'none', editor: 'protocol', zone_editor: 'zone', company: 'none' },
  },
  {
    operations: ['minutes.manage'],
    cells: { reader: 'none', editor: 'protocol', zone_editor: 'none', company: 'none' },
  },
  {
    // 030: «Первое письмо» — как управление доступом: ведущему (владельцу) по области, роли
    // в протоколе его не дают (contracts/api.md 030, «Матрица прав»).
    // 033: рассылка протокола — так же: роли в протоколе её не дают.
    operations: [
      'minutes.access.manage',
      'minutes.transfer',
      'minutes.first_letter',
      'minutes.mailing',
      'minutes.mailing_enable',
    ],
    cells: { reader: 'none', editor: 'none', zone_editor: 'none', company: 'none' },
  },
];

const protocolCellsByOperation = new Map<string, ProtocolCells>(
  protocolRightsMatrix.flatMap((r) => r.operations.map((op) => [op, r.cells] as const)),
);

/** Операции протокола: их ядро прав проверяет и по ролям в протоколе и общему доступу. */
export const protocolOperations: readonly Operation[] = protocolRightsMatrix.flatMap(
  (r) => r.operations,
);

export function isProtocolOperation(operation: Operation): boolean {
  return protocolCellsByOperation.has(operation);
}

/** Правило второй таблицы; операции нет в таблице — «—». */
export function protocolRuleFor(grantee: ProtocolGrantee, operation: Operation): ProtocolRule {
  return protocolCellsByOperation.get(operation)?.[grantee] ?? 'none';
}

/** Роли пользователя в протоколах и общий доступ — для effectivePermissions (меню, /api/auth/me). */
export interface ProtocolGrants {
  readonly roles: readonly ProtocolRole[];
  /** Общий доступ «Все сотрудники» действует (пользователь без роли «Сопровождение»). */
  readonly company: boolean;
  /** 022: внешний пользователь — роли в протоколе дают только externalProtocolOperations. */
  readonly external?: boolean;
}

const NO_PROTOCOL_GRANTS: ProtocolGrants = { roles: [], company: false };

// --- Внешний пользователь (022: contracts/api.md, «Матрица прав»; research, Р3; ADR-0040) ---

/**
 * Что роль в протоколе даёт внешнему пользователю — только сам протокол: чтение, правка по роли
 * и команды протокола целиком у редактора. «Документы», «Контроль» и список доступа — нет.
 * Роли на установку и общий доступ «Все сотрудники» внешнему не действуют.
 */
export const externalProtocolOperations = [
  'minutes.read',
  'minutes.edit',
  'minutes.manage',
] as const satisfies readonly Operation[];

const externalOperationSet: ReadonlySet<string> = new Set(externalProtocolOperations);

/** Даёт ли роль в протоколе эту операцию внешнему пользователю. */
export function isExternalProtocolOperation(operation: Operation): boolean {
  return externalOperationSet.has(operation);
}

/** Какая операция чтения журнала относится к записи с этой чувствительностью. */
export function auditReadOperation(sensitivity: 'access' | 'content'): Operation {
  return sensitivity === 'access' ? 'audit.read.access' : 'audit.read.content';
}

/**
 * Данные для GET /api/rights: роли с пояснениями, матрица с подписями ячеек и операции правила
 * «в сеансе» (FR-011а) — их даёт сам сеанс сверх ячеек.
 */
export function rightsPayload(): {
  roles: { role: Role; title: string; description: string }[];
  inSession: Operation[];
  matrix: {
    operations: Operation[];
    description?: string;
    cells: Record<
      Role,
      { rule: ScopeRule | 'same_as'; label: string; note?: string; sameAs?: Operation[] }
    >;
  }[];
} {
  return {
    roles: roles.map((role) => ({
      role,
      title: roleInfo[role].title,
      description: roleInfo[role].description,
    })),
    inSession: [...sessionOperations],
    matrix: rightsMatrix.map((r) => {
      const cells = Object.fromEntries(
        roles.map((role) => {
          const cell = r.cells[role];
          const view =
            cell.rule === 'same_as'
              ? { rule: cell.rule, label: cell.label, sameAs: cell.sameAs.filter(isOperation) }
              : cell.note
                ? { rule: cell.rule, label: cellLabel(cell), note: cell.note }
                : { rule: cell.rule, label: cellLabel(cell) };
          return [role, view];
        }),
      ) as Record<
        Role,
        { rule: ScopeRule | 'same_as'; label: string; note?: string; sameAs?: Operation[] }
      >;
      const operationsOfRow: Operation[] = [...r.operations];
      return r.description === undefined
        ? { operations: operationsOfRow, cells }
        : { operations: operationsOfRow, description: r.description, cells };
    }),
  };
}
